import { observer } from "mobx-react";
import SettingStore from "../../../../../../../../stores/setting-store";
import TableRow from "../../../../../../../../components/table/components/table-row/table-row";
import useDashboardTranslation from "../../../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { CUSTOMER_ACTIONS } from "../../../../../../../../models/User";
import FontBody2 from "../../../../../../../../components/fonts/font-body2/font-body2";
import Cell from "../cell/cell";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../../../components/fonts/font-props";
import { Input } from "../../../../../../../../components/inputs/input/input";
import { IValidationGroup } from "../../../../../../../../hooks/validation-group/validation-group-hook";
import { Tooltip } from "../../../../../../../../components/tooltip/tooltip";
import Icon from "../../../../../../../../components/icons/icon/icon";
import IconInfo from "../../../../../../../../components/icons/icon-info/icon-info";
import TranslationInput from "../../../../../../../../translation/translation-input/translation-input";

interface IProps {
    storeSetting: SettingStore;
    validationGroup: IValidationGroup;
}

const ShipBackActionTableRow: React.FC<IProps> = observer(({ storeSetting, validationGroup }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const inTransitActionSetting = storeSetting.getInactiveRmasSetting(CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_IN_TRANSIT);
    const deliveredDomesticActionSetting = storeSetting.getInactiveRmasSetting(CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_DELIVERED_DOMESTIC);
    const deliveredInternationalActionSetting = storeSetting.getInactiveRmasSetting(CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_DELIVERED_INTERNATIONAL);

    const isReasonDisabled = () => {
        return !inTransitActionSetting.cancelAfterDays && !deliveredDomesticActionSetting.cancelAfterDays && !deliveredInternationalActionSetting.cancelAfterDays; 
    }

    const isReasonRequired = () => {
        return !!inTransitActionSetting.cancelAfterDays || !!deliveredDomesticActionSetting.cancelAfterDays || !!deliveredInternationalActionSetting.cancelAfterDays; 
    }

    return (
        <TableRow
            skipWrapingCell
            cells={[
                <Cell>
                    <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                        {getTranslation('SHIP_BACK_ITEMS', pageScope)}
                    </FontBody2>
                </Cell>,
                <Cell><></></Cell>,
                <Cell><></></Cell>,

                <Cell disabled={inTransitActionSetting.cancelAfterDays === null}>
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>
                        {getTranslation('TRACKED_SHIPMENT', pageScope)}
                    </FontBody2>
                    <Tooltip content={
                        <div className="inactive-rma-cancel-settings-tooltip-text">
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {getTranslation('TRACKED_SHIPMENT_TOOLTIP_TEXT', pageScope)}
                            </FontBody2>
                        </div>
                    }>
                        <Icon Component={() => <IconInfo color={'var(--color-accent-1)'} />} />
                    </Tooltip>
                </Cell>,
                <Cell>
                    <>
                        <Input
                            type={'number'}
                            value={inTransitActionSetting.cancelAfterDays}
                            onChange={inTransitActionSetting.setCancelAfterDays}
                            min={1}
                            max={999}
                            temp />
                        <FontBody2>{getTranslation('DAYS', pageScope)}</FontBody2>
                    </>
                </Cell>,
                <Cell disabled={isReasonDisabled()}>
                    <div className="cancel-inactive-rma-reason-setting">
                        <TranslationInput 
                            translationKey={inTransitActionSetting.reason} 
                            validationGroup={validationGroup}
                            required={isReasonRequired()}
                        />
                    </div>
                </Cell>,

                <Cell disabled={deliveredDomesticActionSetting.cancelAfterDays === null}>
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>
                        {getTranslation('UNTRACKED_DOMESTIC_SHIPMENT', pageScope)}
                    </FontBody2>
                    <Tooltip content={
                        <div className="inactive-rma-cancel-settings-tooltip-text">
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {getTranslation('UNTRACKED_DOMESTIC_SHIPMENT_TOOLTIP_TEXT', pageScope)}
                            </FontBody2>
                        </div>
                    }>
                        <Icon Component={() => <IconInfo color={'var(--color-accent-1)'} />} />
                    </Tooltip>
                </Cell>,
                <Cell>
                    <>
                        <Input
                            type={'number'}
                            value={deliveredDomesticActionSetting.cancelAfterDays}
                            onChange={deliveredDomesticActionSetting.setCancelAfterDays}
                            min={1}
                            max={999}
                            temp />
                        <FontBody2>{getTranslation('DAYS', pageScope)}</FontBody2>
                    </>
                </Cell>,
                <Cell><></></Cell>,

                <Cell disabled={deliveredInternationalActionSetting.cancelAfterDays === null}>
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>
                        {getTranslation('UNTRACKED_INTERNATIONAL_SHIPMENT', pageScope)}
                    </FontBody2>
                    <Tooltip content={
                        <div className="inactive-rma-cancel-settings-tooltip-text">
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {getTranslation('UNTRACKED_INTERNATIONAL_SHIPMENT_TOOLTIP_TEXT', pageScope)}
                            </FontBody2>
                        </div>
                    }>
                        <Icon Component={() => <IconInfo color={'var(--color-accent-1)'} />} />
                    </Tooltip>
                </Cell>,
                <Cell>
                    <>
                        <Input
                            type={'number'}
                            value={deliveredInternationalActionSetting.cancelAfterDays}
                            onChange={deliveredInternationalActionSetting.setCancelAfterDays}
                            min={1}
                            max={999}
                            temp />
                        <FontBody2>{getTranslation('DAYS', pageScope)}</FontBody2>
                    </>
                </Cell>,
                <Cell><></></Cell>,
            ]}
        />
    );
});

export default ShipBackActionTableRow;